import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Copy, Check, X, Forward } from 'lucide-react';

/**
 * EmailForwardingModal - shown when the user taps the Mail icon in the
 * dashboard header. Mirrors the iOS app's "forward emails into your
 * project inbox" feature: every user gets a unique forwarding address
 * (quotes, invoices, supplier emails) that lands inside their workspace.
 *
 * Address format (must match iOS exactly):
 *   inbox+<supabase-user-uuid>@parse.buildpilot.com.au
 *
 * The `parse.buildpilot.com.au` subdomain is wired to SendGrid Inbound
 * Parse, which webhooks the backend with the full email payload. The
 * backend extracts the user UUID from the `+<uuid>` subaddress and
 * routes the message into that user's workspace inbox.
 */
const buildAddress = (userId) => {
  if (!userId) return 'inbox@parse.buildpilot.com.au';
  return `inbox+${userId}@parse.buildpilot.com.au`;
};

const EmailForwardingModal = ({ open, onClose, userId }) => {
  const [copied, setCopied] = useState(false);
  const address = buildAddress(userId);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* no-op */ }
  };

  return createPortal(
    <div className="bp-workspace">
      <div
        className="bp-modal-overlay"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        data-testid="email-forwarding-modal"
      >
        <div className="bp-modal-sheet bp-modal-sheet-padded" onClick={(e) => e.stopPropagation()}>
          <div className="bp-modal-grabber" aria-hidden="true" />
          <button
            type="button"
            className="bp-modal-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="email-forwarding-close"
          >
            <X size={18} />
          </button>

          <div className="bp-emailfwd-icon" aria-hidden="true">
            <Mail size={28} strokeWidth={1.8} />
          </div>
          <h2 className="bp-modal-title bp-modal-title-center">Forward emails to your build</h2>
          <p className="bp-modal-sub bp-modal-sub-center">
            Forward quotes, invoices, supplier emails and contracts to this address.
            They'll appear in your dashboard inbox automatically.
          </p>

          <div className="bp-emailfwd-address" data-testid="email-forwarding-address">
            <Forward size={16} strokeWidth={2} />
            <span>{address}</span>
          </div>

          <button
            type="button"
            className="bp-emailfwd-copy"
            onClick={copy}
            data-testid="email-forwarding-copy"
          >
            {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy address</>}
          </button>

          <p className="bp-emailfwd-hint">
            Tip: add this as a contact in your email app so you can forward in one tap.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmailForwardingModal;
